// Cart 3x Quantity Controller - Controls quantity in multiples of 3
// Works for both cart page and cart drawer - allows 3, 6, 9, 12, etc.

(function() {
    'use strict';
    
    console.log('🛒 Cart 3x Quantity Controller loading...');
    
    // Configuration - Update these if needed
    const CONFIG = {
      // Identify 3x subscription items by selling plan ID
      threexSellingPlanId: '4675764457',
      
      // Alternative identifiers for 3x subscription
      threexIndicators: [
        'selling_plan=4675764457',
        '3x per month',
        '3x Per Month',
        'Save 20%' // If this is unique to 3x plan
      ],
      
      // Visual indicator options
      indicator: {
        // Options: 'text', 'icon-only', 'dot-only', 'none'
        style: 'icon-only', 
        
        // Text options (if style includes text)
        text: 'Qty in sets of 3',  // Updated text
        fullText: 'Quantity changes in multiples of 3' // Updated text
      },
      
      // Selectors for cart elements
      selectors: {
        cartItems: [
          '.cart-item',
          '.cart__item',
          '[class*="cart-item"]',
          '.line-item',
          // SlideCartHQ drawer items
          '.slidecarthq .item'
        ],
        cartItemLinks: [
          '.cart-item__title a',
          '.cart-item a[href*="selling_plan"]',
          'a[href*="selling_plan"]'
        ],
        cartItemPlans: [
          '.cart-item__plan',
          '.subscription-plan',
          '[class*="plan"]',
          // SlideCartHQ properties area contains plan text
          '.properties-key-value'
        ],
        quantityInputs: [
          'input[name="updates[]"]',
          '.quantity__input',
          'input[type="number"][data-index]',
          // SlideCartHQ quantity input
          '.quantity-selector input'
        ],
        quantityButtons: [
          '.quantity__button',
          'button[name="increment"]',
          'button[name="decrement"]',
          // SlideCartHQ quantity buttons
          '.quantity-selector button'
        ],
        quantityWrappers: [
          '.quantity__wrapper',
          '.cart-item__quantity',
          'quantity-input',
          // SlideCartHQ wrapper
          '.quantity-selector'
        ]
      }
    };
    
    let processedItems = new Set();
    let initAttempts = 0;
    const maxAttempts = 20;
    
    /**
     * Helpers to update Shopify cart and refresh drawer UI
     */
    function getLineKey(cartItem) {
      if (!cartItem || !cartItem.getAttribute) return null;
      return cartItem.getAttribute('data-line-item-key') || cartItem.getAttribute('data-key') || null;
    }

    function formatMoneyFromCents(cents) {
      if (typeof cents !== 'number') return '';
      return `$${(cents / 100).toFixed(2)}`;
    }

    function updateCartDisplay(cart) {
      try {
        if (!cart) return;
        const countEls = document.querySelectorAll('.cart-count');
        countEls.forEach(el => { el.textContent = cart.item_count; });
        const subtotalEl = document.querySelector('.slidecart-subtotal');
        if (subtotalEl && typeof cart.total_price === 'number') {
          subtotalEl.textContent = formatMoneyFromCents(cart.total_price);
        }
      } catch (_) {}
    }

    async function changeCartLineQuantity(lineKey, quantity) {
      if (!lineKey) return;
      const qty = Math.max(0, parseInt(quantity) || 0);
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: qty })
        });
        const cart = await res.json().catch(() => null);
        if (cart) {
          updateCartDisplay(cart);
        }
        // Notify listeners and reprocess after a short delay
        document.dispatchEvent(new Event('cart:updated'));
        processedItems.clear();
        setTimeout(processCartItems, 150);
      } catch (e) {
        console.log('⚠️ Failed to update cart line quantity:', e);
      }
    }

    /**
     * Find element using multiple selectors
     */
    function findElement(selectors, parent = document, multiple = false) {
      for (const selector of selectors) {
        try {
          const elements = multiple ? 
            parent.querySelectorAll(selector) : 
            parent.querySelector(selector);
          
          if (multiple && elements.length > 0) {
            return Array.from(elements);
          } else if (!multiple && elements) {
            return elements;
          }
        } catch (e) {
          // Invalid selector, continue
        }
      }
      return multiple ? [] : null;
    }
    
    /**
     * Check if a cart item is a 3x subscription
     */
    function is3xSubscriptionItem(cartItem) {
      // Method 1: Check the product link for selling plan ID
      const link = findElement(CONFIG.selectors.cartItemLinks, cartItem);
      if (link && link.href) {
        const has3xSellingPlan = link.href.includes(`selling_plan=${CONFIG.threexSellingPlanId}`);
        if (has3xSellingPlan) {
          console.log('✅ Found 3x item by selling plan ID:', link.href);
          return true;
        }
      }
      
      // Method 2: Check subscription plan text
      const planElement = findElement(CONFIG.selectors.cartItemPlans, cartItem);
      if (planElement) {
        const planText = planElement.textContent || '';
        const has3xPlan = CONFIG.threexIndicators.some(indicator => 
          planText.toLowerCase().includes(indicator.toLowerCase())
        );
        if (has3xPlan) {
          console.log('✅ Found 3x item by plan text:', planText.trim());
          return true;
        }
      }
      
      // Method 3: Check entire cart item content for indicators
      const itemText = cartItem.textContent || '';
      const has3xIndicator = CONFIG.threexIndicators.some(indicator => 
        itemText.includes(indicator)
      );
      
      if (has3xIndicator) {
        console.log('✅ Found 3x item by content indicators');
        return true;
      }
      
      return false;
    }
    
    /**
     * Lock quantity for a 3x subscription item to multiples of 3
     */
    function lockQuantityFor3x(cartItem, itemIndex) {
      console.log(`🔒 Locking quantity for 3x item ${itemIndex} to multiples of 3...`);
      
      // Find quantity input
      const quantityInput = findElement(CONFIG.selectors.quantityInputs, cartItem);
      if (!quantityInput) {
        console.log('❌ No quantity input found for item', itemIndex);
        return false;
      }
      
      // Find quantity buttons
      const quantityButtons = findElement(CONFIG.selectors.quantityButtons, cartItem, true);
      
      // Find quantity wrapper for visual indicators
      const quantityWrapper = findElement(CONFIG.selectors.quantityWrappers, cartItem);
      
      // Store original values
      if (!quantityInput.dataset.original3xMin) {
        quantityInput.dataset.original3xMin = quantityInput.getAttribute('min') || '0';
        quantityInput.dataset.original3xMax = quantityInput.getAttribute('max') || '99';
        quantityInput.dataset.original3xStep = quantityInput.getAttribute('step') || '1';
      }
      
      // Get current quantity and ensure it's a multiple of 3
      let currentQty = parseInt(quantityInput.value) || 3;
      const correctedQty = Math.max(3, Math.round(currentQty / 3) * 3);
      
      console.log(`📊 Current qty: ${currentQty}, corrected to: ${correctedQty}`);
      
      // Set to multiples of 3
      quantityInput.value = correctedQty;
      quantityInput.setAttribute('min', '0'); // Allow 0 for removal
      quantityInput.setAttribute('step', '3'); // Only allow multiples of 3
      
      // Don't disable completely, but control the increments
      quantityInput.readOnly = false;
      quantityInput.disabled = false;
      
      // Modify quantity buttons behavior
      quantityButtons.forEach((button, btnIndex) => {
        // Don't disable buttons, but modify their behavior
        button.style.opacity = '1';
        button.style.cursor = 'pointer';
        button.disabled = false;
        
        // Remove existing listeners first
        const newButton = button.cloneNode(true);
        button.parentNode.replaceChild(newButton, button);
        
        // Add custom behavior for 3x increments
        newButton.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          
          const currentVal = parseInt(quantityInput.value) || 3;
          // Robust increment detection for SlideCartHQ
          const aria = (this.getAttribute('aria-label') || '').toLowerCase();
          const isIncrement = aria.includes('increase') || this.name === 'increment' || this.textContent.includes('+');
          
          let newVal;
          if (isIncrement) {
            newVal = currentVal + 3;
          } else {
            // Decrement by 3, allow going to 0 to remove item
            newVal = Math.max(0, currentVal - 3);
          }
          
          console.log(`🔄 3x quantity change: ${currentVal} → ${newVal}`);
          
          if (newVal === 0) {
            console.log('🗑️ Removing 3x item completely (quantity = 0)');
            // Set to 0 to remove the item from cart
            quantityInput.value = 0;
          } else {
            quantityInput.value = newVal;
          }
          
          // Persist to Shopify cart immediately
          const lineKey = getLineKey(cartItem);
          changeCartLineQuantity(lineKey, newVal);
          
          return false;
        }, true);
      });
      
      // Add visual indicator based on config
      if (CONFIG.indicator.style !== 'none' && quantityWrapper && !quantityWrapper.querySelector('.cart-3x-locked-indicator')) {
        if (CONFIG.indicator.style === 'text' || CONFIG.indicator.style === 'icon-text') {
          const indicator = document.createElement('div');
          indicator.className = 'cart-3x-locked-indicator';
          indicator.innerHTML = `<small>Qty in multiples of 3</small>`;
          quantityWrapper.appendChild(indicator);
        }
      }
      
      // Prevent manual input of non-multiples of 3 and persist
      let quantityDebounceTimer = null;
      quantityInput.addEventListener('input', function(e) {
        let val = parseInt(e.target.value);
        
        // Allow 0 for removal
        if (val === 0) {
          console.log('🗑️ Quantity set to 0 for removal');
          if (quantityDebounceTimer) clearTimeout(quantityDebounceTimer);
          const lineKey = getLineKey(cartItem);
          quantityDebounceTimer = setTimeout(() => changeCartLineQuantity(lineKey, 0), 350);
          return;
        }
        
        // Otherwise ensure it's a multiple of 3, minimum 3
        if (isNaN(val) || val < 0) val = 3;
        if (val > 0 && val < 3) val = 3;
        if (val % 3 !== 0) {
          // Round to nearest multiple of 3, minimum 3
          val = Math.max(3, Math.round(val / 3) * 3);
          console.log(`🔧 Corrected quantity to multiple of 3: ${val}`);
          e.target.value = val;
        }
        if (quantityDebounceTimer) clearTimeout(quantityDebounceTimer);
        const lineKey = getLineKey(cartItem);
        quantityDebounceTimer = setTimeout(() => changeCartLineQuantity(lineKey, val), 350);
      });
      
      // Handle blur event to ensure valid value when user finishes typing and persist
      quantityInput.addEventListener('blur', function(e) {
        let val = parseInt(e.target.value);
        
        // Allow 0 for removal
        if (val === 0) {
          console.log('🗑️ Confirming removal (quantity = 0)');
          const lineKey = getLineKey(cartItem);
          changeCartLineQuantity(lineKey, 0);
          return;
        }
        
        // Otherwise ensure it's a valid multiple of 3
        if (isNaN(val) || val < 3 || val % 3 !== 0) {
          val = Math.max(3, Math.round(val / 3) * 3);
          e.target.value = val;
          const lineKey = getLineKey(cartItem);
          changeCartLineQuantity(lineKey, val);
          return;
        }
        const lineKey = getLineKey(cartItem);
        changeCartLineQuantity(lineKey, val);
      });
      
      // Prevent keyboard shortcuts that might bypass validation
      quantityInput.addEventListener('keydown', function(e) {
        // Allow navigation keys, backspace, delete
        const allowedKeys = ['Tab', 'Shift', 'ArrowLeft', 'ArrowRight', 'Backspace', 'Delete', 'Enter'];
        const isNumber = e.key >= '0' && e.key <= '9';
        
        if (!allowedKeys.includes(e.key) && !isNumber) {
          e.preventDefault();
        }
      });
      
      // Add CSS class for styling
      cartItem.classList.add('cart-item-3x-locked');
      
      console.log(`✅ Set 3x multiples for item ${itemIndex} (quantity: ${correctedQty})`);
      return true;
    }
    
    /**
     * Unlock quantity for regular items
     */
    function unlockQuantity(cartItem, itemIndex) {
      console.log(`🔓 Unlocking quantity for regular item ${itemIndex}...`);
      
      const quantityInput = findElement(CONFIG.selectors.quantityInputs, cartItem);
      if (!quantityInput) return;
      
      const quantityButtons = findElement(CONFIG.selectors.quantityButtons, cartItem, true);
      
      // Restore original values
      if (quantityInput.dataset.original3xMin) {
        quantityInput.setAttribute('min', quantityInput.dataset.original3xMin);
        quantityInput.setAttribute('max', quantityInput.dataset.original3xMax);
        quantityInput.disabled = false;
        quantityInput.readOnly = false;
      }
      
      // Re-enable buttons
      quantityButtons.forEach(button => {
        button.disabled = false;
        button.style.opacity = '';
        button.style.cursor = '';
        button.style.pointerEvents = '';
      });
      
      // Remove indicator
      const indicator = cartItem.querySelector('.cart-3x-locked-indicator');
      if (indicator) indicator.remove();
      
      // Remove CSS class
      cartItem.classList.remove('cart-item-3x-locked');
    }
    
    /**
     * Process all cart items
     */
    function processCartItems() {
      console.log('🔍 Processing cart items...');
      
      const cartItems = findElement(CONFIG.selectors.cartItems, document, true);
      console.log(`📦 Found ${cartItems.length} cart items`);
      
      let controlled3xItems = 0;
      let regularItems = 0;
      
      cartItems.forEach((cartItem, index) => {
        const lineKey = cartItem.getAttribute && (cartItem.getAttribute('data-line-item-key') || cartItem.getAttribute('data-key'));
        const itemId = lineKey || cartItem.id || `item-${index}`;
        
        // Skip if already processed (unless it's been updated)
        if (processedItems.has(itemId)) {
          return;
        }
        
        if (is3xSubscriptionItem(cartItem)) {
          if (lockQuantityFor3x(cartItem, index)) {
            controlled3xItems++;
            processedItems.add(itemId);
          }
        } else {
          unlockQuantity(cartItem, index);
          regularItems++;
          processedItems.add(itemId);
        }
      });
      
      console.log(`✅ Processed ${cartItems.length} items: ${controlled3xItems} controlled (3x multiples), ${regularItems} regular`);
      return cartItems.length > 0;
    }
    
    /**
     * Setup cart monitoring
     */
    function setupCartMonitoring() {
      console.log('👁️ Setting up cart monitoring...');
      
      // Monitor for cart updates (AJAX cart changes)
      const observer = new MutationObserver(function(mutations) {
        let shouldReprocess = false;
        
        mutations.forEach(mutation => {
          // Check if cart items were added/removed/modified
          if (mutation.type === 'childList') {
            const target = mutation.target;
            if (target.closest && 
                (target.closest('.cart') || 
                 target.closest('.cart-drawer') ||
                 target.closest('[class*="cart"]'))) {
              shouldReprocess = true;
            }
          }
          
          // Check if quantity values changed
          if (mutation.type === 'attributes' && 
              mutation.attributeName === 'value' &&
              mutation.target.matches && 
              mutation.target.matches('input[name="updates[]"]')) {
            shouldReprocess = true;
          }
        });
        
        if (shouldReprocess) {
          console.log('🔄 Cart changes detected, reprocessing items...');
          // Clear processed items to recheck everything
          processedItems.clear();
          setTimeout(processCartItems, 100);
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value', 'class']
      });
      
      // Also listen for specific cart events if available
      document.addEventListener('cart:updated', function() {
        console.log('🔄 Cart updated event detected');
        processedItems.clear();
        setTimeout(processCartItems, 200);
      });
      
      // Listen for cart drawer open/close
      document.addEventListener('cart-drawer:open', function() {
        console.log('🔄 Cart drawer opened');
        setTimeout(processCartItems, 300);
      });
      
      console.log('✅ Cart monitoring setup complete');
    }
    
    /**
     * Intercept cart update requests
     */
    function setupCartUpdateInterception() {
      console.log('🛡️ Setting up cart update interception...');
      
      // Intercept fetch requests to cart/update
      const originalFetch = window.fetch;
      window.fetch = function(...args) {
        let url = args[0];
        let options = args[1] || {};
        const urlStr = typeof url === 'string' ? url : (url && url.url) || '';
        const isUpdate = urlStr.includes('/cart/update');
        const isChange = urlStr.includes('/cart/change');

        if (isUpdate || isChange) {
          console.log('🛡️ Intercepting cart update/change request');

          const adjustToMultipleOf3 = (qty) => {
            if (qty === 0) return 0;
            const n = parseInt(qty);
            if (isNaN(n)) return 3;
            if (n <= 0) return 0;
            return Math.max(3, Math.round(n / 3) * 3);
          };

          const enforceFor3xLines = (updatesArrayLike) => {
            const cartItems = findElement(CONFIG.selectors.cartItems, document, true);
            cartItems.forEach((cartItem, index) => {
              if (!is3xSubscriptionItem(cartItem)) return;
              if (updatesArrayLike[index] == null) return;
              const currentQty = parseInt(updatesArrayLike[index]);
              const corrected = adjustToMultipleOf3(currentQty);
              if (currentQty !== corrected) {
                console.log(`🛡️ Correcting 3x item ${index} quantity: ${currentQty} → ${corrected}`);
                updatesArrayLike[index] = corrected.toString();
              }
            });
          };

          try {
            if (options.body instanceof FormData) {
              if (isUpdate) {
                const updates = options.body.getAll('updates[]');
                enforceFor3xLines(updates);
                // Rewrite all updates back by index
                updates.forEach((val, i) => {
                  options.body.set(`updates[${i}]`, val);
                });
              } else if (isChange) {
                const qty = options.body.get('quantity');
                if (qty != null) {
                  const corrected = adjustToMultipleOf3(parseInt(qty));
                  options.body.set('quantity', String(corrected));
                }
              }
            } else if (typeof options.body === 'string') {
              // Try JSON first
              let handled = false;
              try {
                const obj = JSON.parse(options.body);
                if (isUpdate) {
                  if (obj && obj.updates && typeof obj.updates === 'object') {
                    // obj.updates may be a map of line IDs to qty; leave as-is (unknown mapping)
                  }
                } else if (isChange) {
                  if ('quantity' in obj) {
                    obj.quantity = adjustToMultipleOf3(obj.quantity);
                    options.body = JSON.stringify(obj);
                    handled = true;
                  }
                }
              } catch (_) {}
              if (!handled) {
                const params = new URLSearchParams(options.body);
                if (isUpdate) {
                  const values = params.getAll('updates[]');
                  if (values && values.length) {
                    const arr = Array.from(values);
                    enforceFor3xLines(arr);
                    arr.forEach((val, i) => {
                      params.set(`updates[${i}]`, val);
                    });
                    options.body = params.toString();
                  }
                } else if (isChange) {
                  if (params.has('quantity')) {
                    const corrected = adjustToMultipleOf3(parseInt(params.get('quantity')));
                    params.set('quantity', String(corrected));
                    options.body = params.toString();
                  }
                }
              }
            }
          } catch (e) {
            console.log('⚠️ Error adjusting cart update/change request:', e);
          }
        }
        return originalFetch.call(this, url, options);
      };
      
      console.log('✅ Cart update interception setup complete');
    }
    
    /**
     * Main initialization
     */
    function init() {
      initAttempts++;
      console.log(`🚀 Initializing Cart 3x Controller (attempt ${initAttempts}/${maxAttempts})...`);
      
      const foundItems = processCartItems();
      
      if (foundItems || initAttempts >= maxAttempts) {
        setupCartMonitoring();
        setupCartUpdateInterception();
        
        console.log('🎉 Cart 3x Quantity Controller initialized successfully!');
        
        // Expose debug functions
        window.cart3xController = {
          processCartItems,
          is3xSubscriptionItem,
          lockQuantityFor3x,
          unlockQuantity,
          config: CONFIG,
          debug: () => {
            console.log('📋 Cart 3x Controller Debug:');
            const cartItems = findElement(CONFIG.selectors.cartItems, document, true);
            console.log(`- Found ${cartItems.length} cart items`);
            cartItems.forEach((item, i) => {
              const qtyInput = findElement(CONFIG.selectors.quantityInputs, item);
              const currentQty = qtyInput ? qtyInput.value : 'N/A';
              const is3x = is3xSubscriptionItem(item);
              console.log(`  ${i}: Qty=${currentQty}, 3x subscription=${is3x}, Multiples of 3=${is3x && currentQty % 3 === 0}`);
            });
          }
        };
        
        console.log('🛠️ Debug functions available at window.cart3xController');
        console.log('💡 Try: window.cart3xController.debug()');
        
      } else {
        console.log(`⏳ No cart items found yet, retrying in 1 second... (${initAttempts}/${maxAttempts})`);
        setTimeout(init, 1000);
      }
    }
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        setTimeout(init, 500); // Wait for cart to load
      });
    } else {
      setTimeout(init, 500);
    }
    
  })();